import type { IWFVersionDto } from "@kuika/kuika-cl-model/src/dtos/workflow.dto";

/**
 * Row view-model + pure helpers for the Version History tab.
 *
 * Kept free of React and of the service layer so the list rules — which versions are
 * shown, which one is "latest", what the search matches — are testable on their own.
 */

/** Exactly two versions can be diffed against each other. */
export const VERSION_COMPARE_COUNT: number = 2;

export interface IVersionRow {
  id: string;
  /** Raw number from the API; rendered as "V{n}". */
  versionNumber: string;
  /** May be empty — rendered as `UNNAMED_VERSION_LABEL` below. */
  name: string;
  /** May be empty — rendered as `ADD_DESCRIPTION_LABEL` below. */
  description: string;
  /** ISO-8601 export instant; empty when the backend predates the field. */
  exportedAt: string;
  /** Exporting user's display name; empty when unresolvable. */
  exportedByName: string;
  exportedByPhotoUrl: string;
  isActive: boolean;
  isLatest: boolean;
}

/**
 * Preview builds are collapsed into the version they were exported as, so they are not
 * rows of their own. Two signals, by doctrine (2026-08-11): `isPreviewVersion` is the
 * authority for preview provenance (only a preview build ever writes it true), and a
 * dotted version number ("N.x") is the preview NUMBERING shape — a dotted number is
 * never a user-facing export version, so both shapes hide. The flag is optional on the
 * DTO (older backends omit it), hence the explicit `=== true`: absent means "not known
 * to be a preview", never "hide".
 */
const isPreviewBuild = (version: IWFVersionDto): boolean =>
  version.isPreviewVersion === true || version.versionNumber.includes(".");

/**
 * Index of the newest version. Derived from the highest version *number* rather than the
 * API's ordering, so the LATEST chip cannot land on the wrong row if that ordering ever
 * changes. `parseInt` with an explicit radix is locale-independent. Falls back to the
 * first row when no version number parses.
 */
const findLatestIndex = (versions: readonly IWFVersionDto[]): number => {
  let latestIndex: number = 0;
  let highest: number = Number.NEGATIVE_INFINITY;
  versions.forEach((version: IWFVersionDto, index: number): void => {
    const parsed: number = parseInt(version.versionNumber, 10);
    if (!Number.isNaN(parsed) && parsed > highest) {
      highest = parsed;
      latestIndex = index;
    }
  });
  return latestIndex;
};

export const toVersionRows = (versions: readonly IWFVersionDto[]): IVersionRow[] => {
  const listed: IWFVersionDto[] = versions.filter((version: IWFVersionDto): boolean => !isPreviewBuild(version));
  const latestIndex: number = findLatestIndex(listed);
  return listed.map((version: IWFVersionDto, index: number): IVersionRow => ({
    id: version.id,
    versionNumber: version.versionNumber,
    name: version.name ?? "",
    description: version.description ?? "",
    exportedAt: version.createdAt ?? "",
    exportedByName: version.createdUserName ?? "",
    exportedByPhotoUrl: version.createdUserPhotoUrl ?? "",
    // The API's flag is a soft-delete used as the active/inactive switch: ActivateVersion
    // clears it, DeactivateVersion sets it.
    isActive: !version.isDeleted,
    isLatest: index === latestIndex
  }));
};

/**
 * Search key. Locale-INDEPENDENT on purpose (the same reasoning as the process gallery):
 * `toLowerCase()` — never `toLocaleLowerCase()` — then strip combining marks, so "İlk"
 * matches a user typing "ilk". Without the NFD pass, lowercasing "İ" yields "i" + U+0307
 * and the query would never match.
 */
const normalizeForSearch = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/** Matches the design's "Search name or description" — both fields, blank means no filter. */
export const filterVersionRows = (rows: readonly IVersionRow[], query: string): IVersionRow[] => {
  const trimmed: string = query.trim();
  if (!trimmed) return [...rows];
  const needle: string = normalizeForSearch(trimmed);
  return rows.filter(
    (row: IVersionRow): boolean =>
      normalizeForSearch(row.name).includes(needle) || normalizeForSearch(row.description).includes(needle)
  );
};

export const canCompareVersions = (selectedIds: readonly string[]): boolean =>
  selectedIds.length === VERSION_COMPARE_COUNT;

/**
 * Placeholder copy for the two editable cells. Defined here, not in the components, so the
 * table and the modals cannot drift to different wording for the same empty state.
 */
export const UNNAMED_VERSION_LABEL: string = "Unnamed Version";
export const ADD_DESCRIPTION_LABEL: string = "Add description";

/**
 * "May 1, 2026, 1:00 PM" — the viewer's own timezone, but a fixed "en-US" ordering, so the
 * same instant reads the same on every machine the designer runs on (the branching timelines
 * settled on the same rule). Display only; never fed back into identifiers.
 * Empty or unparseable input degrades to "" so the cell can fall back to its placeholder.
 */
export const formatExportedAt = (isoInstant: string): string => {
  if (!isoInstant) return "";
  const date: Date = new Date(isoInstant);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
};

/**
 * "Ada Lovelace" → "AL", "Ada" → "AD". Uppercased with the locale-independent
 * `toUpperCase` (same convention as board-avatar) so a Turkish system locale cannot
 * produce a dotted-İ surprise for ASCII names.
 */
export const exporterInitials = (name: string): string => {
  const trimmed: string = name.trim();
  if (!trimmed) return "";
  const parts: string[] = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** "V4 · İK Onay Adımı" — identifies one version in a modal title or a diff pane label. */
export const versionLabel = (version: IVersionRow): string => {
  const name: string = version.name.trim() || UNNAMED_VERSION_LABEL;
  return `V${version.versionNumber} · ${name}`;
};

export interface IVersionComparePair {
  older: IVersionRow;
  newer: IVersionRow;
}

/**
 * Orders the two selected versions oldest-first, so a diff always reads left-to-right in time.
 * The order the selection arrives in is NOT trusted — it follows the table's rows, which are
 * newest-first. `parseInt` with an explicit radix is locale-independent; when either number is
 * unparseable there is no defensible ordering, so the incoming order stands.
 */
export const toComparePair = (versions: readonly IVersionRow[] | undefined): IVersionComparePair | undefined => {
  if (!versions || versions.length !== VERSION_COMPARE_COUNT) return undefined;
  const first: IVersionRow = versions[0];
  const second: IVersionRow = versions[1];
  const firstNumber: number = parseInt(first.versionNumber, 10);
  const secondNumber: number = parseInt(second.versionNumber, 10);
  if (!Number.isNaN(firstNumber) && !Number.isNaN(secondNumber) && secondNumber < firstNumber) {
    return { older: second, newer: first };
  }
  return { older: first, newer: second };
};

/**
 * The selection toolbar shows an action only when the selection has rows it can apply to:
 * "Deactivate" needs at least one active row, "Activate" at least one inactive row. A
 * uniform selection therefore gets a single verb and a mixed one gets both — the toolbar
 * follows the selection's status instead of offering a no-op.
 */
export const hasDeactivatableSelection = (rows: readonly IVersionRow[], selectedIds: readonly string[]): boolean =>
  rows.some((row: IVersionRow): boolean => selectedIds.includes(row.id) && row.isActive);

export const hasActivatableSelection = (rows: readonly IVersionRow[], selectedIds: readonly string[]): boolean =>
  rows.some((row: IVersionRow): boolean => selectedIds.includes(row.id) && !row.isActive);

/**
 * Multi-select machinery (checkboxes, the selection toolbar) only earns its place once
 * there are at least two versions — compare needs two, and a bulk action on a single row
 * duplicates that row's own actions menu.
 */
export const isSelectionEnabled = (rows: readonly IVersionRow[]): boolean => rows.length >= VERSION_COMPARE_COUNT;
